import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Empty, Spin, message, Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import styled from 'styled-components';
import getToken from '../../../../logic/utils/getToken';
import ConfigCard from './components/ConfigCard';
import CreateConfigModal from './components/CreateConfigModal';
import ConfigEditor from './components/ConfigEditor';

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

function Configs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get('/api/configs', { headers: { token } });
      if (res.data.status === 'success') setItems(res.data.data);
    } catch (e) {
      message.error('Ошибка загрузки списка конфигов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDownload = async (item) => {
    try {
      const token = getToken();
      const res = await axios.get(`/api/configs/${item.filename}`, { headers: { token } });
      if (res.data.status !== 'success') throw new Error();
      const blob = new Blob([res.data.data.content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error('Не удалось скачать файл');
    }
  };

  const handleDelete = async (item) => {
    try {
      const token = getToken();
      const res = await axios.delete(`/api/configs/${item.filename}`, { headers: { token } });
      if (res.data.status === 'success') {
        message.success('Файл удалён');
        fetchList();
      }
    } catch (e) {
      message.error('Ошибка удаления');
    }
  };

  const handleOpen = (item) => setEditing(item);

  const handleCreate = () => setCreateOpen(true);

  const handleCreated = (filename) => {
    setCreateOpen(false);
    fetchList();
    setEditing({
      filename,
      format: filename.split('.').pop(),
      size: 0,
      mtime: new Date().toISOString(),
    });
  };

  return (
    <PageContainer>
      <HeaderRow>
        <Title level={3} style={{ margin: 0 }}>
          Конфигурационные файлы
          {!loading && items.length > 0 && (
            <Text
              type="secondary"
              style={{ fontSize: 16, marginLeft: 12, fontWeight: 400 }}
            >
              {`(${items.length})`}
            </Text>
          )}
        </Title>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Добавить файл
        </Button>
      </HeaderRow>
      <div style={{ height: 24 }} />
      {loading && (
        <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>
      )}
      {!loading && items.length === 0 && (
        <Empty description="Нет конфигов" />
      )}
      {!loading && items.length > 0 && (
        <CardsGrid>
          {items.map((it) => (
            <ConfigCard
              key={it.filename}
              item={it}
              onOpen={handleOpen}
              onDownload={handleDownload}
              onDelete={handleDelete}
            />
          ))}
        </CardsGrid>
      )}
      <CreateConfigModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
      <ConfigEditor
        file={editing}
        onClose={() => setEditing(null)}
        onSaved={fetchList}
      />
    </PageContainer>
  );
}

export default Configs;
